import type {
  Cycle,
  CycleType,
  Phase,
} from "@src/packages/cycles/hooks/useCycles";

export type CryoCycleRole = "adds_to_vault" | "uses_from_vault" | "neither";

const CYCLES_THAT_ADD_TO_VAULT = new Set<CycleType>([
  "egg_freezing",
  "embryo_freezing",
  "ivf",
  "icsi",
]);

const CYCLES_THAT_CAN_USE_FROZEN_MATERIAL = new Set<CycleType>(["ivf", "icsi"]);

type OocyteSpecimen = NonNullable<Cycle["oocyteSpecimens"]>[number];
type EmbryoTransferEvidence = Pick<
  NonNullable<Phase["embryoTransferInfo"]>,
  "embryoType"
>;
type PhaseCryoEvidence = {
  embryoTransferInfo?: EmbryoTransferEvidence | null;
};
type SpecimenUsageEvidence = Pick<
  NonNullable<OocyteSpecimen["specimenUsages"]>[number],
  "usage"
>;
type OocyteSpecimenCryoEvidence = {
  specimenUsages: readonly SpecimenUsageEvidence[];
};

/**
 * Resolves how the current cycle relates to long-term cryostorage.
 *
 * The API identifies FET cycles directly. For IVF/ICSI, a transfer phase with
 * a frozen embryo or thaw usage on an oocyte specimen is the available API
 * signal that this cycle consumes vault material rather than adding to it.
 *
 * Care surfaces use this only to gate the Laboratory card (shown for cycles
 * that pass through the fertility lab); it deliberately says nothing about
 * cycle phase, so downstream copy stays stable across the whole cycle.
 */
export function getCryoCycleRole(
  cycleType: CycleType | null | undefined,
  phases: readonly PhaseCryoEvidence[] = [],
  oocyteSpecimens: readonly OocyteSpecimenCryoEvidence[] = [],
): CryoCycleRole {
  if (cycleType === "fet") return "uses_from_vault";

  if (
    cycleType &&
    CYCLES_THAT_CAN_USE_FROZEN_MATERIAL.has(cycleType) &&
    (phases.some(
      (phase) => phase.embryoTransferInfo?.embryoType === "frozen",
    ) ||
      oocyteSpecimens.some((specimen) =>
        specimen.specimenUsages.some((usage) => usage.usage === "thaw"),
      ))
  ) {
    return "uses_from_vault";
  }

  if (cycleType && CYCLES_THAT_ADD_TO_VAULT.has(cycleType)) {
    return "adds_to_vault";
  }

  return "neither";
}
